import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, CheckCheck, RefreshCw } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import type { Alert, Tenant } from "../../lib/types";
import { fmtDateTime } from "../../lib/utils";
import { FilterChip } from "../core/Layout";
import { Button } from "../core/Button";
import { Plate, StatusBadge } from "../core/Badges";
import { EscalationBadge } from "../shared/EscalationBadge";

export function ClientAlertsTab({ tenant }: { tenant: Tenant }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState("open");
  const [scanning, setScanning] = useState(false);
  const { data, isLoading } = useQuery<Alert[]>({ queryKey: ["alerts", tenant.id, status, ""], queryFn: async () => (await api.get("/alerts", { params: { tenant_id: tenant.id, status: status || undefined } })).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["alerts"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); qc.invalidateQueries({ queryKey: ["tenant", tenant.id] }); qc.invalidateQueries({ queryKey: ["tenants"] }); };
  const update = async (a: Alert, s: "acknowledged" | "resolved") => {
    try { await api.put(`/alerts/${a.id}`, { status: s }); toast.success(`Alert ${s}`); refresh(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const scan = async () => {
    setScanning(true);
    try { const { data: r } = await api.post("/alerts/scan", null, { params: { tenant_id: tenant.id } }); toast.success(`${r.created} new alert(s) · ${r.reminders} reminder(s) sent`); refresh(); } catch (e) { toast.error(errorMessage(e)); } finally { setScanning(false); }
  };
  return (
    <div data-testid="client-alerts-tab">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">{[["open", "Open"], ["acknowledged", "Acknowledged"], ["resolved", "Resolved"], ["", "All"]].map(([s, l]) => <FilterChip key={s} active={status === s} onClick={() => setStatus(s)} testId={`client-alert-status-${s || "all"}`}>{l}</FilterChip>)}</div>
        <Button variant="secondary" onClick={scan} loading={scanning} data-testid="client-alerts-scan-button"><RefreshCw className="h-3.5 w-3.5" />Scan now</Button>
      </div>
      <div className="space-y-2" data-testid="client-alerts-list">
        {isLoading && <p className="text-sm text-slate-400">Loading…</p>}
        {data && data.length === 0 && <div className="card-crisp py-12 text-center text-sm text-slate-500">No alerts in this view.</div>}
        {(data || []).map((a) => (
          <article key={a.id} className="card-crisp flex flex-wrap items-start justify-between gap-3 p-3.5" data-testid={`client-alert-card-${a.id}`}>
            <div className="flex min-w-0 items-start gap-3">
              <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${a.severity === "warning" ? "bg-[color:var(--gold)]" : a.severity === "critical" ? "bg-orange-500" : "bg-rose-600"}`} />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2"><StatusBadge status={a.severity} /><StatusBadge status={a.status} /><EscalationBadge a={a} /></div>
                <p className="mt-1 text-sm font-semibold text-slate-900">{a.title}</p>
                <p className="text-xs text-slate-600">{a.message}</p>
                <p className="mt-1 text-[11px] text-slate-400">{fmtDateTime(a.created_at)}{a.acknowledged_by ? ` · ack by ${a.acknowledged_by}` : ""}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {a.vehicle_id && <Link to={`/bo/vehicles/${a.vehicle_id}`}><Plate plate={a.plate_number} /></Link>}
              {a.status === "open" && <Button size="sm" variant="secondary" onClick={() => update(a, "acknowledged")} data-testid={`client-ack-alert-${a.id}`}><Check className="h-3.5 w-3.5" />Ack</Button>}
              {a.status !== "resolved" && <Button size="sm" variant="ghost" onClick={() => update(a, "resolved")} data-testid={`client-resolve-alert-${a.id}`}><CheckCheck className="h-3.5 w-3.5" />Resolve</Button>}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
